import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, useMapEvents } from 'react-leaflet';
import styled from 'styled-components';

interface LocationData {
  lat: number;
  lon: number;
  batt?: number;
  acc?: number;
  alt?: number;
  vel?: number;
  tst: number;
  tid?: string;
  _type: string;
  conn?: string;
  topic?: string;
}

const MapWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #F5F3EF;
`;

const TitleOverlay = styled.div`
  position: absolute;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: none;
  
  h1 {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: #8B5CF6;
    margin: 0;
    letter-spacing: 0.02em;
    text-transform: none;
    text-shadow: 
      0 1px 3px rgba(0, 0, 0, 0.8),
      0 0 8px rgba(139, 92, 246, 0.3);
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
`;

const SimpleMapContainer = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  
  .leaflet-container {
    height: 100%;
    width: 100%;
  }
`;

const MapSection = styled.div`
  flex: 1;
  position: relative;
`;

const DebugInfo = styled.div`
  padding: 1rem;
  background-color: #F5F3EF;
  color: #3E3E3E;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  text-align: center;
`;

const Map: React.FC = () => {
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(4);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const API_KEY = process.env.REACT_APP_OWNTRACKS_API_KEY || '44621738';
      
      // Use proxy in development, multiple CORS proxies in production
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      if (isDevelopment) {
        // Development: use local proxy
        const apiUrl = `/api/0/locations?key=${API_KEY}`;
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setLocations(data);
        setError(null);
        return;
      }
      
      // Production: try multiple CORS proxies
      const targetUrl = `http://147.182.207.191:8083/api/0/locations?key=${API_KEY}`;
      const corsProxies = [
        `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(targetUrl)}`,
        `https://thingproxy.freeboard.io/fetch/${encodeURIComponent(targetUrl)}`
      ];
      
      let lastError;
      
      for (const proxyUrl of corsProxies) {
        try {
          console.log(`Trying CORS proxy: ${proxyUrl}`);
          const response = await fetch(proxyUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          // Handle different response formats based on proxy
          const locations = Array.isArray(data) ? data : (data.contents ? JSON.parse(data.contents) : data);
          setLocations(locations);
          setError(null);
          console.log(`Successfully fetched data using: ${proxyUrl}`);
          return;
          
        } catch (err) {
          console.warn(`CORS proxy failed: ${proxyUrl}`, err);
          lastError = err;
          continue;
        }
      }
      
      // If all proxies fail, throw the last error
      throw lastError || new Error('All CORS proxies failed');
      
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(`Failed to fetch location data: ${err instanceof Error ? err.message : 'Unknown error'}. This may be due to CORS restrictions or server issues.`);
    } finally {
      setLoading(false);
    }
  };

  // Convert locations to polyline coordinates for path visualization
  const pathCoordinates: [number, number][] = locations
    .sort((a, b) => a.tst - b.tst)
    .map(loc => [loc.lat, loc.lon]);

  // Get center point for map
  const getMapCenter = (): [number, number] => {
    if (locations.length === 0) return [37.7749, -122.4194]; // Default to San Francisco
    
    const latSum = locations.reduce((sum, loc) => sum + loc.lat, 0);
    const lonSum = locations.reduce((sum, loc) => sum + loc.lon, 0);
    
    return [latSum / locations.length, lonSum / locations.length];
  };

  // Component to track zoom level changes
  const ZoomTracker: React.FC = () => {
    useMapEvents({
      zoomend: (e) => {
        setZoom(e.target.getZoom());
      },
    });
    return null;
  };

  // Calculate line weight based on zoom level
  const getLineWeight = (zoomLevel: number): number => {
    if (zoomLevel <= 3) return 1;
    if (zoomLevel <= 5) return 2;
    if (zoomLevel <= 8) return 3;
    if (zoomLevel <= 12) return 4;
    return 5;
  };

  if (loading) {
    return (
      <MapWrapper>
        <TitleOverlay>
          <h1>Everywhere I've Wandered</h1>
        </TitleOverlay>
        <DebugInfo>
          <div>🔄 LOADING LOCATION DATA...</div>
        </DebugInfo>
      </MapWrapper>
    );
  }

  if (error) {
    return (
      <MapWrapper>
        <TitleOverlay>
          <h1>Everywhere I've Wandered</h1>
        </TitleOverlay>
        <DebugInfo>
          <div>❌ ERROR: {error}</div>
          <div><button onClick={fetchLocations}>Try Again</button></div>
        </DebugInfo>
      </MapWrapper>
    );
  }

  return (
    <MapWrapper>
      <TitleOverlay>
        <h1>Everywhere I've Wandered</h1>
      </TitleOverlay>
      <MapSection>
        <SimpleMapContainer>
          <MapContainer
            center={getMapCenter()}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
          >
            <ZoomTracker />
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            
            {/* Draw path line connecting locations chronologically */}
            {pathCoordinates.length > 1 && (
              <Polyline
                positions={pathCoordinates}
                color="#8B5CF6"
                weight={getLineWeight(zoom)}
                opacity={1}
              />
            )}
          </MapContainer>
        </SimpleMapContainer>
      </MapSection>
    </MapWrapper>
  );
};

export default Map; 